import React from 'react'
import * as tippy from 'tippy.js'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  DeadlineStickerEntry,
  DeadlineStickerValue,
  HubLike,
  StickerColor,
  StickerSettingsInBoard
} from 'yougile-model'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {Gantt} from '@extensions/gantt'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {StickerHub} from '@extensions/sticker/views-web/a-sticker-view'
import {getTimerTime} from '@extensions/sticker/views-web/deadline-view'

export const getDeadlineStickerColor = (
  value?: DeadlineStickerValue
): StickerColor => {
  if (!value?.current?.deadline) {
    return 'sticker-gray'
  }

  const {deadline, withTime, startDate} = value.current
  const now = withTime ? Time.now() : Time.startOfDay(Date.now())
  const deadlineTime = withTime ? deadline : Time.startOfDay(deadline)

  if (now > deadlineTime) {
    return 'sticker-red'
  }

  if (now === deadlineTime) {
    return 'sticker-orange'
  }

  if (startDate) {
    const startDateTime = withTime ? startDate : Time.startOfDay(startDate)
    if (now >= startDateTime) {
      return 'sticker-orange'
    }
  }

  if (
    !startDate &&
    withTime &&
    Time.startOfDay(Date.now()) === Time.startOfDay(deadline)
  ) {
    return 'sticker-orange'
  }

  return 'sticker-gray'
}

export const deadlineTimerView = (
  task: StickerHub,
  defaultValue: DeadlineStickerValue,
  settings?: StickerSettingsInBoard
): boolean => {
  if (!defaultValue?.current?.deadline) {
    return false
  }

  const deadlineTimestamp = defaultValue.current.deadline ?? 0
  const deadline =
    defaultValue.current.withTime ?? 0
      ? deadlineTimestamp
      : Time.endOfDay(deadlineTimestamp) - Time.minute
  const lessThenDay = Math.abs(deadline - Time.now()) < Time.day
  const isTaskCompleted =
    task?.isCompleted ?? task?.data?.gtd?.state?.stage === 'done'
  return Boolean(settings?.hasDefaultState) && lessThenDay && !isTaskCompleted
}

export const getStickerDeadlineTitle = (
  name: string,
  defaultValue?: DeadlineStickerValue,
  task?: StickerHub,
  settings?: StickerSettingsInBoard
): React.ReactNode => {
  if (!task) {
    return name
  }

  if (!defaultValue) {
    if (DeadlineCtl.getBlockedPoints(task).includes('start')) {
      const hint = <EmptyBlockedDeadlineHint task={task} />
      return <BlockedPointText hint={hint} />
    }

    return name
  }

  const deadlineTimestamp = defaultValue?.current?.deadline ?? 0
  const deadline =
    defaultValue?.current?.withTime ?? 0
      ? deadlineTimestamp
      : Time.endOfDay(deadlineTimestamp) - Time.minute

  if (deadlineTimerView(task, defaultValue, settings)) {
    return getTimerTime(deadline)
  } else {
    return formatDeadline(task, defaultValue.current)
  }
}

export const getPanelDeadlineTitle = (values: string[]) => {
  return values[0] === values[1]
    ? Time.formatDate(+values[0])
    : `${Time.formatDate(+values[0])}-${Time.formatDate(+values[1])}`
}

export const formatDeadline = (
  task: StickerHub,
  value: DeadlineStickerEntry
): React.ReactNode => {
  const {deadline, withTime, startDate} = value || ({} as DeadlineStickerEntry)

  const blockedPoints = DeadlineCtl.getBlockedPoints(task)
  const startBlocked = blockedPoints.includes('start')
  const startBlockedHint = Lang.msg.timeline.stickerDeadlineStartLockedTooltip

  const timeSuff = withTime
    ? ` ${Lang.msg.at} ` + Time.formatShort(deadline, true)
    : ''

  if (!startDate && !deadline) {
    if (startBlocked) {
      const hint = <EmptyBlockedDeadlineHint task={task} />
      return <BlockedPointText hint={hint} />
    } else {
      return timeSuff
    }
  } else if (!startDate) {
    const text = Time.formatDate(deadline) + timeSuff

    if (startBlocked) {
      return <BlockedPointText text={text} hint={startBlockedHint} />
    } else {
      return text
    }
  } else if (!deadline) {
    const text = Time.formatDate(startDate) + timeSuff

    if (startBlocked) {
      return <BlockedPointText text={text} hint={startBlockedHint} />
    } else {
      return text
    }
  } else {
    const fromD = Time.toLocalDate(startDate)
    const toD = Time.toLocalDate(deadline)
    const currYear = new Date().getFullYear()
    const alwaysWithYear =
      fromD.getFullYear() !== currYear || toD.getFullYear() !== currYear
    const startDateText = Time.formatDate(startDate, {alwaysWithYear})
    const deadlineText = Time.formatDate(deadline, {alwaysWithYear})

    if (startBlocked) {
      return (
        <div className="min-w-0 flex items-center">
          <BlockedPointText text={startDateText} hint={startBlockedHint} />
          <span className="whitespace-pre"> — </span>
          <span className="truncate">
            {deadlineText}
            {timeSuff}
          </span>
        </div>
      )
    } else {
      return startDateText + ' — ' + deadlineText + timeSuff
    }
  }
}

export const EmptyBlockedDeadlineHint = ({task}: {task: HubLike}) => (
  <div className="max-w-256 break-words">
    {Lang.msg.timeline.stickerDeadlineBlockedBy}:{' '}
    {Gantt.getLinkedFromHubs(task)
      .map(x => (x.title.length < 20 ? x.title : x.title.slice(0, 20) + '…'))
      .join(', ')}
  </div>
)

export const BlockedPointText = ({
  text,
  hint
}: {
  text?: string
  hint?: React.ReactNode
}) => (
  <>
    <Tooltip
      content={hint}
      // hide all other tooltips to prevent multiple tooltips at once on a sticker
      onShow={() => tippy.hideAll({duration: 0})}
      offset={[0, 6]}
      placement="bottom"
    >
      <Icon size={16} icon="IconLock" color="acid-red" />
    </Tooltip>
    {text && <span className="ml-2 text-sticker-red">{text}</span>}
  </>
)
